import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { StaffHeader } from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Cross, Printer, Smartphone, Video, Clock, FileDown } from "lucide-react";
import { downloadElementPdf } from "@/lib/pdf";

const PHARMACY = {
  name: "Pharmacie des Écoles",
  address: "8 Place André Allègre, 83250 La Londe-les-Maures",
  phone: "04 94 66 00 00",
};

export default function Poster() {
  const url = useMemo(() => (typeof window !== "undefined" ? window.location.origin + "/pharmacie" : "/pharmacie"), []);
  const [pdfing, setPdfing] = useState(false);
  const savePdf = async () => {
    setPdfing(true);
    try { await downloadElementPdf(document.getElementById("print-area"), "affiche-teleconsultation.pdf"); }
    finally { setPdfing(false); }
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="no-print"><StaffHeader /></div>

      <div className="no-print mx-auto max-w-6xl px-4 pt-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Affiche QR pour le comptoir</h1>
          <p className="text-sm text-muted-foreground">Aperçu A4 ci-dessous. Le QR code ouvre le parcours patient : <span className="font-mono">{url}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={savePdf} disabled={pdfing} data-testid="pdf-poster-btn" variant="outline" className="rounded-xl h-12 gap-2"><FileDown className="h-5 w-5" /> {pdfing ? "Génération…" : "Enregistrer en PDF"}</Button>
          <Button onClick={() => window.print()} data-testid="print-poster-btn" className="rounded-xl h-12 gap-2"><Printer className="h-5 w-5" /> Imprimer l'affiche</Button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6 flex justify-center">
        <div id="print-area" data-testid="poster-a4"
          className="bg-white text-slate-900 shadow-xl border border-slate-200 rounded-2xl overflow-hidden flex flex-col"
          style={{ width: "210mm", minHeight: "297mm" }}>
          <div className="h-3 bg-emerald-600 w-full" />
          <div className="flex-1 flex flex-col items-center text-center px-16 py-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-14 w-14 rounded-2xl bg-emerald-600 grid place-items-center text-white"><Cross className="h-8 w-8" /></div>
              <div className="text-left">
                <div className="text-3xl font-heading font-bold tracking-tight">{PHARMACY.name}</div>
                <div className="text-emerald-700 font-semibold">Espace de téléconsultation</div>
              </div>
            </div>

            <h2 className="font-heading text-5xl font-bold tracking-tight mt-10 leading-tight">Téléconsultez<br />près de chez vous</h2>
            <p className="text-xl text-slate-600 mt-6 max-w-xl">Scannez pour organiser votre téléconsultation dans notre espace dédié.</p>

            <div className="mt-12 p-6 rounded-3xl border-4 border-emerald-600 bg-white" data-testid="poster-qr">
              <QRCodeSVG value={url} size={340} level="M" bgColor="#ffffff" fgColor="#064e3b" includeMargin={false} />
            </div>
            <div className="mt-4 inline-flex items-center gap-2 text-emerald-700 font-semibold text-lg"><Smartphone className="h-5 w-5" /> Pointez l'appareil photo de votre téléphone</div>

            <div className="grid grid-cols-3 gap-6 mt-14 w-full max-w-2xl text-slate-700">
              <Step icon={Video} n="1" t="Trouvez votre rendez-vous médical" />
              <Step icon={Clock} n="2" t="Réservez la cabine à cette heure" />
              <Step icon={Cross} n="3" t="Présentez-vous à la pharmacie" />
            </div>

            <div className="mt-auto pt-14 text-slate-500">
              <p className="font-semibold text-slate-700">{PHARMACY.address}</p>
              <p>Tél. {PHARMACY.phone}</p>
              <p className="text-sm mt-2">Espace confidentiel et accompagné. En cas d'urgence, appelez le 15 ou le 112.</p>
            </div>
          </div>
          <div className="h-3 bg-emerald-600 w-full" />
        </div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, n, t }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="h-12 w-12 rounded-full bg-emerald-50 grid place-items-center text-emerald-700 relative">
        <Icon className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-emerald-600 text-white text-xs grid place-items-center font-bold">{n}</span>
      </div>
      <p className="text-sm font-medium leading-tight">{t}</p>
    </div>
  );
}
